/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo } from 'react';
import { Validator } from './Validator';
import { useField } from 'react-final-form';
import {
   minMaxLengthValidator,
   minMaxValueValidator,
   multiValidator,
   requiredValidator,
   validateValidator
} from './Validators';
import { FieldRenderProps, FieldState, FieldValidator } from './FinalFormTypes';
import { useForm } from './Form';

export interface FormItemProps<T> {
   name: string;
   inline?: boolean;
   label?: string;
   hint?: string;
   helpText?: string;
   required?: boolean;
   min?: number;
   max?: number;
   minLength?: number;
   maxLength?: number;
   validator?: Validator;
   dataTypeValidator?: Validator;
   triggerOnBlur?: boolean;
   format?: (value: T | undefined, name: string) => string;
   parse?: (value: string | undefined, name: string) => T | undefined;
   isEqual?: (a: T | string, b: T | string) => boolean;
   render: (fieldRenderProps: FieldRenderProps<T | undefined, HTMLElement, any>) => React.ReactElement;
}

export const getFieldErrorMessage = (touchedOrDirty: boolean | undefined, error: any | undefined, submitError: any | undefined) => {
   return submitError === undefined ? (touchedOrDirty ? error : undefined) : submitError;
};

export const getFieldHasError = (touchedOrDirty: boolean | undefined, error: any | undefined, submitError: any | undefined) => {
   return getFieldErrorMessage(touchedOrDirty, error, submitError) !== undefined;
};

export const getFieldErrorClassName =  () => {
   return 'uf-form-item-has-error';
};

export const FormItem = <T,>(props: FormItemProps<T>) => {

   const { required, dataTypeValidator, min, max, minLength, maxLength, validator, triggerOnBlur=true } = props;

   const form = useForm<T>();
   const validateFields = form.isAutoSaveForm() ? [] : undefined;

   const validate = useMemo(() => multiValidator([
                        requiredValidator(required),
                        validateValidator(dataTypeValidator),
                        minMaxValueValidator(min, max),
                        minMaxLengthValidator(minLength, maxLength),
                        validateValidator(validator)
                     ])
   , [required, dataTypeValidator, min, max, minLength, maxLength, validator]);

   const fieldValidator: FieldValidator<T | undefined> = useCallback(<T,>(value: T, allValues: any, meta?: FieldState<T>) => {
      return validate(value);
   },[validate]);

   const fieldRenderProps = useField<T | undefined, HTMLElement, any>(props.name, {
      formatOnBlur: false,
      format: props.format,
      parse: props.parse,
      isEqual: props.isEqual,
      validateFields: validateFields,
      validate: fieldValidator,
      subscription: {
         submitError: true,
         touched: true,
         dirty: triggerOnBlur ? undefined : true,
         error: true,
         value: true
      }
   });

   const { submitError, touched, dirty, error } = fieldRenderProps.meta;
   useEffect(()=>{
      fieldRenderProps['fieldErrorClassName'] = getFieldHasError(touched || dirty, error, submitError) ? getFieldErrorClassName() : undefined;
   },[fieldRenderProps, submitError, touched, dirty, error]);

   const onBlur = fieldRenderProps.input.onBlur;

   fieldRenderProps.input.onBlur = useCallback((event?: React.FocusEvent<HTMLElement>) => {
      const cancelButtonCausedBlur = (event && event.relatedTarget && (event.relatedTarget as HTMLElement).getAttribute('data-iscancelbutton'));
      if (cancelButtonCausedBlur) {
         return;
      }

      if (props.triggerOnBlur) {
         validate(fieldRenderProps.input.value);
      }

      if (onBlur) {
         onBlur(event);
      }
   },[fieldRenderProps.input.value, onBlur, props.triggerOnBlur, validate]);

   return (
      <>
         {props.render(fieldRenderProps)}
      </>
   );
};
